'use client';

import { AppImageClient } from '@components/appImage';

import { BigCardProps } from './types';
import { Button } from '../../button';

import IconLinkButton from '@/assets/icons/iconLinkButton.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

const containerClasses: Record<string, string> = {
  medium: 'w-[358px] flex-grow max-sm:h-[512px] max-sm:w-full',
  large: 'w-[768px] flex-grow max-sm:mt-[62px] max-sm:h-[344px] max-sm:w-full',
  flexible: 'w-full h-full flex-grow ',
};

const imageClasses: Record<string, string> = {
  medium: 'top-2 right-0 left-0 mx-auto w-[352px]',
  large: 'pointer-events-none top-[-39%] left-0 max-sm:top-[-42%]',
  secondary: 'pointer-events-none top-0 left-0 w-[100%] max-sm:top-0',
};

const descriptionClasses: Record<string, string> = {
  medium: 'w-[304px]',
  large: 'w-[450px] max-sm:w-[304px]',
};

export const BigCard = ({
  titleId,
  title,
  descriptionId,
  description,
  imageSrc,
  imageData,
  imageSize,
  children,
  variant = 'medium',
  isHorizontal = false,
  buttonLink,
  buttonId,
  buttonText,
  alt,
  customClassNames,
  buttonVariant = 'secondary',
  additionalContent,
}: BigCardProps) => {
  const hasButton = buttonText || buttonLink;

  return (
    <>
      <div
        className={classNames({
          cls: 'group rounded-20 bg-surface-base-secondary relative box-border',
          mods: {
            [containerClasses.medium]: variant === 'medium',
            [containerClasses.large]: variant === 'large',
            [containerClasses.flexible]: variant === 'flexible',
          },
          additional: [customClassNames?.main],
        })}
      >
        <div
          className={classNames({
            cls: 'rounded-20 bg-surface-base-secondary top-0 flex h-full w-full justify-start from-white/20 to-white/5 p-[8px] max-sm:flex-col',
            mods: {
              'flex-col': !isHorizontal,
              'flex-row-reverse': isHorizontal,
            },
            additional: [customClassNames?.mainWrapper],
          })}
        >
          {imageSrc || imageData ? (
            <div
              className={classNames({
                cls: 'rounded-20 overflow-hidden max-sm:w-full',
                mods: {
                  'w-[50%]': isHorizontal,
                },
                additional: [customClassNames?.imgWrapper],
              })}
            >
              {imageData && imageSize ? (
                <AppImageClient
                  {...imageData}
                  width={imageSize.width}
                  height={imageSize.height}
                  className={classNames({
                    mods: {
                      [imageClasses.medium]: variant === 'medium',
                      [imageClasses.large]: variant === 'large',
                      [imageClasses.secondary]: variant === 'secondary',
                      [imageClasses.flexible]: variant === 'flexible',
                    },
                    additional: [customClassNames?.img],
                  })}
                  alt={alt}
                />
              ) : (
                <img
                  src={imageSrc}
                  className={classNames({
                    mods: {
                      [imageClasses.medium]: variant === 'medium',
                      [imageClasses.large]: variant === 'large',
                      [imageClasses.secondary]: variant === 'secondary',
                      [imageClasses.flexible]: variant === 'flexible',
                    },
                    additional: [customClassNames?.img],
                  })}
                  alt={alt}
                />
              )}
            </div>
          ) : (
            <div
              className={classNames({
                cls: 'rounded-20 w-full overflow-hidden',
                additional: [customClassNames?.contentWrapperChildren],
              })}
            >
              {children}
            </div>
          )}

          {additionalContent}

          {(title || description || hasButton) && (
            <div
              className={classNames({
                cls: 'flex flex-col justify-between gap-5 p-[28px] max-sm:w-full max-sm:p-5',
                mods: {
                  'w-[50%] !p-[32px]': isHorizontal,
                },
                additional: [customClassNames?.contentWrapper],
              })}
            >
              <div>
                {title && (
                  <p
                    id={titleId}
                    className={classNames({
                      cls: 'mb-3 flex text-3xl font-medium max-sm:text-2xl max-sm:leading-7',
                      additional: [customClassNames?.title],
                    })}
                  >
                    {title}
                  </p>
                )}
                {description && (
                  <div
                    id={descriptionId}
                    className={classNames({
                      cls: '[&>a]:text-text-base-primary text-base font-normal tracking-tight max-sm:text-sm [&>a]:underline',
                      mods: {
                        [descriptionClasses.medium]: variant === 'medium',
                        [descriptionClasses.large]: variant === 'large',
                      },
                    })}
                    dangerouslySetInnerHTML={{ __html: description! }}
                  />
                )}
              </div>
              <div>
                {hasButton && (
                  <Button
                    id={buttonId}
                    className="w-fit gap-1 !pt-3 !pr-4 !pb-3 !pl-5"
                    variant={buttonVariant}
                    href={buttonLink}
                    target="_blank"
                    ariaLabel={buttonText}
                  >
                    {buttonText} <IconLinkButton />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
